import React, { useEffect } from "react";
import { useAdminStore } from "../../../store/useAdminStore";
import { FolderType, ImageType } from "@/types/productTypes";
import FolderSidebar from "./folder/FolderSidebar";
import ImageGrid from "./image/ImageGrid";

const ImageManager: React.FC = () => {
   const {
      allImages,
      fetchImages,
      updateImage,
      deleteImage,
      assignImageToFolder,
      folders,
      fetchFolders,
      createFolder,
      deleteFolder,
      selectedFolder,
      setSelectedFolder,
      updateFolder,
   } = useAdminStore();
   useEffect(() => {
      fetchImages();
      fetchFolders();
   }, [fetchImages, fetchFolders]);

   const handleDropImage = async (folderId: number, imageId: number) => {
      try {
         await assignImageToFolder(imageId, folderId);
      } catch (error) {
         console.error("Error assigning image to folder", error);
      }
   };

   const handleCreateFolder = async (name: string, parentId?: number) => {
      try {
         await createFolder({ name, parentId });
      } catch (error) {
         console.error("Error creating folder", error);
      }
   };

   const handleDeleteImage = async (id: number) => {
      try {
         await deleteImage(id);
      } catch (error) {
         console.error("Error deleting image", error);
      }
   };

   const handleEditImage = async (image: ImageType) => {
      try {
         await updateImage(image.id, {
            title: image.title,
            description: image.description,
            altText: image.altText,
         });
         // Optionally show a success toast
      } catch (error) {
         console.error("Error updating image:", error);
         // Optionally show an error toast
      }
   };

   const handleDeleteFolder = async (folderId: number) => {
      try {
         await deleteFolder(folderId);
         // If we're in the folder that was just deleted, navigate to parent
         if (selectedFolder === folderId) {
            const folder = folders.find((f) => f.id === folderId);
            handleNavigate(folder?.parentId || null);
         }
      } catch (error) {
         console.error("Error deleting folder:", error);
      }
   };

   const handleNavigate = (folderId: number | null) => {
      setSelectedFolder(folderId);
   };

   const adaptFolder = (folder: any): FolderType => ({
      ...folder,
      createdAt: folder.createdAt || new Date().toISOString(),
      updatedAt: folder.updatedAt || new Date().toISOString(),
   });

   const handleEditFolder = async (data: Partial<FolderType>) => {
      if (!data.id) return; // Early return if no ID
      try {
         await updateFolder(data.id, {
            name: data.name || "",
            description: data.description,
         });
      } catch (error) {
         console.error("Error updating folder:", error);
      }
   };

   return (
      <div className="flex h-screen">
         <FolderSidebar
            folders={folders.map(adaptFolder)}
            onDropImage={handleDropImage}
            selectedFolder={selectedFolder}
            onSelectFolder={(folder) => handleNavigate(folder.id)}
            onCreateFolder={handleCreateFolder}
         />
         <ImageGrid
            folders={folders.map(adaptFolder)}
            images={allImages}
            onDelete={handleDeleteImage}
            onDeleteFolder={handleDeleteFolder}
            onEdit={handleEditImage}
            onEditFolder={handleEditFolder}
            onNavigate={handleNavigate}
            currentFolderId={selectedFolder}
         />
      </div>
   );
};

export default ImageManager;
